import { axiosInstance } from "../../../api/axiosInstance.tsx";
import axios from 'axios';

export const fetchLelang = async (): Promise<any[]> => {
  try {
    const response = await axiosInstance.get("/lelang/view_lelang");
    return response.data.map((lelang) => ({
      id: lelang.id,
      boldText: lelang.lelangName,
      textColumn1: lelang.lelangAddress,
      textColumn2: lelang.lelangType,
      textColumn3: lelang.lelangContactNumber,
      textColumn4: lelang.lelangEmail,
      status: lelang.lelangStatus,
    }));
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
      "Gagal mengambil data lelang. Silakan coba lagi."
    );
  }
};

export interface lelangSearchParams {
  lelangName?: string;
  lelangAddress?: string;
  lelangContactNumber?: string;
  lelangEmail?: string;
  lelangType?: string;
  lelangTaxId?: string;
  lelangBankDetails?: string;
  lelangStatus?: string;
  limit?: number;
  offset?: number;
  keyword?: string;
  orderBy?: string;
  order?: string;
}

export const getLelang = async (params: lelangSearchParams = {}) => {
  const response = await axiosInstance.get("/building/bidding-contract/search_bidding_contracts", {
    params,
  });
  return response.data;
};

// Step 1: Create Lelang
export const createLelang = async (data) => {
  // Normalize vendor names to array
  const vendorNamesArray = Array.isArray(data.vendorNames)
    ? data.vendorNames
    : data.vendorNames
    ? [data.vendorNames]
    : [];

  // Normalize vendor project values to array (preserve order with vendorNamesArray)
  const vendorProjectValueArray = Array.isArray(data.vendorProjectValue)
    ? data.vendorProjectValue
    : data.vendorProjectValue !== undefined && data.vendorProjectValue !== null
    ? [data.vendorProjectValue]
    : [];

  // Ensure vendorDocument is always an array (backend may iterate with .map)
  let vendorDocumentArray: string[] = [];
  if (Array.isArray(data.vendorDocument)) {
    vendorDocumentArray = data.vendorDocument
      .flat()
      .filter(Boolean)
      .map((x: any) => String(x));
  } else if (typeof data.vendorDocument === "string") {
    vendorDocumentArray = data.vendorDocument
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  // Format date safely (fallback to raw if already string)
  const lelangDateFormatted = data.lelangDate
    ? (typeof data.lelangDate === "string"
        ? data.lelangDate
        : new Date(data.lelangDate).toISOString())
    : null;

  // Build vendorProjectValue as object mapping vendorId -> value
  const vendorProjectValueObj = vendorNamesArray.reduce((acc: any, vendorId: string, idx: number) => {
    const val = vendorProjectValueArray[idx];
    if (vendorId && val !== undefined && val !== null && val !== "") {
      acc[vendorId] = val;
    }
    return acc;
  }, {} as Record<string, any>);

  // Simple YYYY-MM-DD formatter
  const toDateOnly = (v: any) => {
    if (!v) return null;
    const d = typeof v === "string" ? new Date(v) : new Date(v);
    if (isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  };

  // Ensure salesProjectId is a UUID; fallback to projectName if that holds the UUID
  const isUUID = (v: string) => /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(v || "");
  const salesProjectUUID = isUUID(data?.salesProjectId)
    ? data.salesProjectId
    : (isUUID(data?.projectName) ? data.projectName : undefined);

  const payload = {
    salesProjectId: salesProjectUUID,
    projectName: data.projectName, // gunakan nama proyek sebenarnya
    clientName: data.clientName,
    projectId: data.projectId,
    projectType: data.projectType,
    projectAddress: data.projectAddress,
    latitude: data.latitude,
    longitude: data.longitude,
    region: data.lelangRegion,
    city: data.lelangCity,
    district: data.lelangDistrict,
    constructionPhase: data.constructionPhase,
    bidAmount: data.rabValue,
    startDate: toDateOnly(data.startDate),
    endDate: toDateOnly(data.endDate),
    lelangDate: toDateOnly(lelangDateFormatted || data.lelangDate),
    projectProcess: data.projectProcess,
    vendorNames: vendorNamesArray,
    vendorProjectValue: vendorProjectValueObj,
    lelangStatus: data.lelangStatus,
    // Jangan kirim nama file asli saat create; file akan diupload dan server mengembalikan key terenkripsi
    biddingFile: [],
    vendorDocument: [],
    contractStatus: data.contractStatus,
  };

  const response = await axiosInstance.post(
    `building/bidding-contract/create_bidding_contract`,
    payload
  );
  return response.data;
};

// Create Office Location
export const createLelangLocation = async (data) => {
  const response = await axiosInstance.post(
    `/lelangLocations/create_lelang_location`,
    data
  );
  return response.data; // contains the office `id` or `code`
};

// Step 2: Upload Photo by Item ID
export const uploadItemPhoto = async (id, file) => {
  const formData = new FormData();
  formData.append("photo", file);

  return axiosInstance.post(`/lelang/upload_photo/${id}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const fetchLelangPhotoBlob = async (
  filename: string
): Promise<string | null> => {
  try {
    const token = localStorage.getItem("token");

    const response = await axiosInstance.get(`/lelang/photo/${filename}`, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    console.log(response.data);
    const objectUrl = URL.createObjectURL(response.data);
    return objectUrl;
  } catch (error) {
    console.error("Gagal mengambil foto:", error);
    return null;
  }
};

export const uploadProposal = async (
  id: string,
  file: File,
  type: "biddingFile" | "vendorDocument"
) => {
  console.log(`=== [UploadProposal] Start upload → id=${id}, type=${type}, file=${file.name}, size=${file.size} bytes`);

  const formData = new FormData();
  // backend mengharapkan field "file"
  formData.append("file", file);

  try {
    const response = await axiosInstance.post(
      `building/bidding-contract/upload_file/${id}/${type}`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    console.log("=== [UploadProposal] Response dari backend ===");
    console.log(response.data);
    return response.data;
  } catch (error) {
    console.error(`Error uploading ${type}:`, error);
    throw error;
  }
};


export const getProposalFile = async (filename: string) => {
  try {
    const response = await axiosInstance.get(
      `building/bidding-contract/file/${encodeURIComponent(filename)}`,
      {
        responseType: 'blob',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        }
      }
    );

    // Verify we received a blob
    if (!(response.data instanceof Blob)) {
      throw new Error('Invalid file format received');
    }

    return {
      blob: response.data,
      filename: getFilenameFromHeaders(response.headers) || filename,
      contentType: response.headers['content-type'] || 'application/octet-stream'
    };

  } catch (error) {
    console.error(`Error fetching proposal file ${filename}:`, error);

    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        throw new Error('Proposal file not found');
      }
      if (error.response?.status === 403) {
        throw new Error('Permission denied to access this file');
      }
    }

    throw new Error('Failed to download proposal file');
  }
};

// Helper function to extract filename from headers
const getFilenameFromHeaders = (headers) => {
  const contentDisposition = headers['content-disposition'];
  if (!contentDisposition) return null;

  const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
  if (matches && matches[1]) {
    return matches[1].replace(/['"]/g, '');
  }
  return null;
};

// Delete lelang
export const deleteLelang = async (id: string) => {
  try {
    await axiosInstance.delete(`/building/bidding-contract/delete_bidding_contract/${id}`);
  } catch (error) {
    throw (
      error.response?.data?.message || "Terjadi kesalahan saat menghapus lelang"
    );
  }
};

export const deleteProposalFile = async (id: string) => {
  try {
    await axiosInstance.delete(`building/bidding-contract/delete_file/${id}`);
  } catch (error) {
    throw (
      error.response?.data?.message || "Terjadi kesalahan saat menghapus lelang location"
    );
  }
};

export const deleteLelangLocation = async (id: number) => {
  try {
    await axiosInstance.delete(`/lelangLocations/delete_lelang_location/${id}`);
  } catch (error) {
    throw (
      error.response?.data?.message || "Terjadi kesalahan saat menghapus lelang location"
    );
  }
};

export const updateLelang = async (data) => {
  // Ensure vendorNames is always an array
  const vendorNamesArray = Array.isArray(data.vendorNames)
    ? data.vendorNames
    : data.vendorNames
      ? [data.vendorNames]
      : [];

  // Normalize vendor project values to array
  const vendorProjectValueArray = Array.isArray(data.vendorProjectValue)
    ? data.vendorProjectValue
    : data.vendorProjectValue !== undefined && data.vendorProjectValue !== null
    ? [data.vendorProjectValue]
    : [];

  // Prepare vendorDocument only if provided by caller (preserve positions; don't filter empties)
  let vendorDocumentArray: (string | null)[] | undefined = undefined;
  if (data.vendorDocument !== undefined) {
    if (Array.isArray(data.vendorDocument)) {
      vendorDocumentArray = data.vendorDocument
        .flat()
        .map((x: any) => (x === undefined ? null : x === null ? null : String(x)));
    } else if (typeof data.vendorDocument === "string") {
      vendorDocumentArray = data.vendorDocument
        .split(",")
        .map((s) => s.trim())
        .map((s) => (s.length ? s : null));
    } else {
      vendorDocumentArray = [];
    }
  }

  // Build vendorProjectValue as object mapping vendorId -> value
  const vendorProjectValueObj = vendorNamesArray.reduce((acc: any, vendorId: string, idx: number) => {
    const val = vendorProjectValueArray[idx];
    if (vendorId && val !== undefined && val !== null && val !== "") {
      acc[vendorId] = val;
    }
    return acc;
  }, {} as Record<string, any>);

  const toDateOnly = (v: any) => {
    if (!v) return null;
    const d = typeof v === "string" ? new Date(v) : new Date(v);
    if (isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  };

  const payload: any = {
    salesProjectId: data.salesProjectId,
    projectName: data.projectName,
    clientName: data.clientName,
    projectId: data.projectId,
    projectType: data.projectType,
    projectAddress: data.projectAddress,
    latitude: data.latitude,
    longitude: data.longitude,
    region: data.lelangRegion,
    city: data.lelangCity,
    district: data.lelangDistrict,
    constructionPhase: data.constructionPhase,
    bidAmount: data.rabValue,
    startDate: toDateOnly(data.startDate),
    endDate: toDateOnly(data.endDate),
    lelangDate: toDateOnly(data.lelangDate),
    // keep old submissionDate if provided, else omit
    submissionDate: data.submissionDate ? toDateOnly(data.submissionDate) : undefined,
    projectProcess: data.projectProcess,
    vendorNames: vendorNamesArray,
    vendorProjectValue: vendorProjectValueObj,
    lelangStatus: data.lelangStatus,
    contractStatus: data.contractStatus,
  };
  if (vendorDocumentArray !== undefined) {
    payload.vendorDocument = vendorDocumentArray;
  }

  // If encrypted bidding file key is provided, include it
  if (data.biddingFile) {
    payload.biddingFile = Array.isArray(data.biddingFile)
      ? data.biddingFile
      : [data.biddingFile];
  }

  const response = await axiosInstance.put(

    `/building/bidding-contract/update_bidding_contract/${data.id}`,
    payload
  );
  return response.data;
};

// Fetch Lelang Details by ID
export const getLelangById = async (id) => {
  try {

    const response = await axiosInstance.get(`/building/bidding-contract/view_bidding_contract/${id}`);
    console.log("=== [getLelangById] Response data ===");
    console.log(response.data);
    return response.data;
  } catch (error) {
    throw (
      error.response?.data?.message ||
      "Terjadi kesalahan saat mengambil data lelang"
    );
  }
};

// Fungsi untuk mencari lelang berdasarkan keyword
export const searchLelang = async (searchQuery: string) => {
  try {
    const response = await axiosInstance.get(
      `/bidding/search_bidding?keyword=${searchQuery}`
    );
    return response.data; // Pastikan response sesuai format yang diterima tabel
  } catch (error) {
    console.error("Error fetching lelang:", error);
    throw (
      error.response?.data?.message ||
      "Terjadi kesalahan saat mengambil data karyawan"
    );
  }
};

export const fetchVendors = async (): Promise<any[]> => {
  try {
    const response = await axiosInstance.get("/vendors/view_vendors");
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
      "Gagal mengambil data vendor. Silakan coba lagi."
    );
  }
};
